import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ConnectField,
  ElementLayoutPatch,
  ElementSlot,
  InteractionMode,
  LayersDropPosition,
  LayersTreeNode,
  LayoutSelection,
  InspectorSelection,
  SelectedElement,
  usesLayoutGrammar,
} from "./studioTypes";
import { NO_TAG, isTextTag } from "../components/studioTags";
import { parseParsleyRef } from "../components/studioParsley";

export type LayersFlatRow = {
  node: LayersTreeNode;
  depth: number;
  // Tag of the enclosing element row, if any. A text node uses it to tell
  // whether a text element above it already identifies the text (an <h1>), or
  // whether it is loose in a container (<div>hello</div>) and has to stand in
  // for the missing text element itself.
  parentTagName: string | null;
  label: string;
  hasChildren: boolean;
  expanded: boolean;
  selected: boolean;
  selectable: boolean;
  draggable: boolean;
};

// Tags that can never receive children — mirrors the bridge's void-element
// guard so invalid "inside" drops are rejected before a command is posted.
const VOID_ELEMENT_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

const isLayersTreeNode = (node: any): node is LayersTreeNode =>
  !!node &&
  typeof node === "object" &&
  typeof node.id === "string" &&
  typeof node.kind === "string" &&
  Array.isArray(node.children);

type Args = {
  interactionMode: InteractionMode;
  postCommandToBridge: (cmd: any) => void;
  applyBridgeSelection: (next: {
    studioId?: string;
    fieldZuid: string;
    fieldType?: string;
    itemZuid?: string;
    modelZuid?: string;
  }) => void;
  applyLayoutSelection: (next: {
    codeId?: string;
    layoutId?: string;
    breadcrumb?: { layoutId?: string; label: string }[];
  }) => void;
  applyInspectorSelection: (next: InspectorSelection) => void;
  refreshInspectorSlots: (
    nodeId: string,
    tagName: string,
    slots: ElementSlot[],
    layoutPatch: ElementLayoutPatch | null,
    ownsLinkControls: boolean,
    preserveValues: boolean
  ) => void;
  codeFileNameById: Record<string, string>;
  fieldsState: Record<string, any>;
  // The field the user picked, keyed `codeId::layoutId::slotKey`. Supplied by
  // StudioWrapper so a freshly connected row can be labelled with the real field
  // rather than a name recovered from the expression.
  connectedBySlot: Record<string, ConnectField>;
  selectedElement: SelectedElement | null;
  selectedLayout: LayoutSelection | null;
  inspectorSelection: InspectorSelection | null;
  dndDisabled: boolean;
};

// Content rows the tree treats as "the element's text": a static text run, or a
// field rendering inline (an attribute binding is a field row too, but it is not
// the element's text).
const isInlineTextNode = (node: LayersTreeNode) =>
  node.kind === "text" || (node.kind === "field" && !node.attr);

// Give every un-wrapped run of text a text element to live under.
//
// The bridge reports the DOM as it is, so `<div>hello</div>` arrives as a text
// row hanging directly off a container, while `<h1>hello</h1>` arrives as a text
// element with the text nested inside it. Those are the same thing to a user —
// text, and the tag around it — so the tree shouldn't render them as two
// different shapes. Here we insert a placeholder text element wherever one is
// missing, giving both cases the identical container/value pair:
//
//   <h1>hello</h1>     h1       →  hello
//   <div>hello</div>   div → No Tag → hello
//
// The placeholder is PRESENTATIONAL. It deliberately carries no layoutPatch,
// which is what makes its Tag selector read-only downstream (canChangeTag keys
// off layoutPatch.isSelf).
//
// Giving it a tag would have to WRAP the text in a new element, and that is a
// different operation from the tag swap this panel does — one we can't perform
// yet. Every address in the patch layer is a data-layout-id, and loose text
// doesn't have one: addressing it means indexing text runs positionally, which
// is only sound when the template and the rendered DOM have the same shape.
// Parsley loops and conditionals break that, and guessing wrong writes the edit
// into the wrong place in the customer's source. So the tag stays read-only
// until positional addressing is built with a correspondence check behind it.
const withTextElementPlaceholders = (
  nodes: LayersTreeNode[],
  parentTagName: string | null
): LayersTreeNode[] =>
  nodes.map((node) => {
    const next: LayersTreeNode = {
      ...node,
      children: withTextElementPlaceholders(node.children, node.tagName),
    };

    // Already inside a text element (or is not text at all) — nothing to do.
    if (!isInlineTextNode(next) || isTextTag(parentTagName)) return next;

    return {
      id: `${next.id}:noTag`,
      kind: "element",
      tagName: NO_TAG,
      codeId: next.codeId,
      // No layout id of its own: this element does not exist in the markup.
      layoutId: null,
      layoutPatch: null,
      // An array (even empty) is what makes a node open the Inspector, so the
      // placeholder behaves like any other element row: click it, see its tag.
      slots: [],
      children: [next],
    };
  });

// A node the Inspector panel can open: a supported element (its `slots` array
// is set — attributes and/or the Tag selector, even when empty for a plain
// heading or the No Tag placeholder), a text node carrying an editable Text
// slot, or — in layout mode only — a dynamic field row, whose slot holds the
// underlying code reference. In content mode a field row still routes to the
// content editor instead.
const isPanelNode = (node: LayersTreeNode, interactionMode: InteractionMode) =>
  Array.isArray(node.slots) &&
  (node.kind === "element" ||
    node.kind === "text" ||
    (node.kind === "field" && usesLayoutGrammar(interactionMode)));

// A leaf element's addressable content rows — the text runs and bound fields
// carrying its Text ("Value") slot. Recognized by their layoutPatch addressing
// the leaf element (that's how the bridge marks a leaf's own runs), so a nested
// element's text never matches.
const findTextSlotChildren = (node: LayersTreeNode): LayersTreeNode[] => {
  const matches: LayersTreeNode[] = [];
  const visit = (children: LayersTreeNode[]) => {
    children.forEach((child) => {
      if (
        child.layoutPatch?.layoutId === node.layoutId &&
        child.layoutPatch?.codeId === node.codeId &&
        (child.slots || []).some((slot) => slot.kind === "text")
      ) {
        matches.push(child);
      }
      visit(child.children);
    });
  };
  visit(node.children);
  return matches;
};

// A leaf element's single content row (see findTextSlotChildren). Returns null
// when the element has no such row, or has several — each run is then
// reachable only through its own tree row.
const findLoneTextSlotChild = (node: LayersTreeNode): LayersTreeNode | null => {
  const matches = findTextSlotChildren(node);
  return matches.length === 1 ? matches[0] : null;
};

// How many content rows a leaf element has (see findTextSlotChildren). Zero
// means it is empty; two or more means mixed content.
const countTextSlotChildren = (node: LayersTreeNode): number =>
  findTextSlotChildren(node).length;

// The row whose panel a canvas click on a layout element opens: the element
// itself when it owns a panel, or — for a bare element (no panel, no slots) —
// its lone text run / bound field. Null when neither is panel-worthy, i.e. the
// Inspector has nothing to edit.
const findLayoutPanelTarget = (
  node: LayersTreeNode,
  interactionMode: InteractionMode
): LayersTreeNode | null => {
  const ownsPanel =
    isPanelNode(node, interactionMode) && !!node.layoutPatch?.isSelf;
  const textChild =
    ownsPanel || node.slots?.length ? null : findLoneTextSlotChild(node);
  const target =
    textChild && isPanelNode(textChild, interactionMode) ? textChild : node;
  return isPanelNode(target, interactionMode) ? target : null;
};

// The slots an Inspector panel shows for a node: its own, plus — in layout mode,
// for an element whose content is a lone text run or bound field — that row's
// Value slot, so Tag and Value share one panel. Mixed content is unchanged.
const getPanelSlots = (
  node: LayersTreeNode,
  interactionMode: InteractionMode
): ElementSlot[] | undefined => {
  if (
    !usesLayoutGrammar(interactionMode) ||
    node.kind !== "element" ||
    !isPanelNode(node, interactionMode) ||
    !node.layoutPatch?.isSelf
  ) {
    return node.slots;
  }
  const loneChild = findLoneTextSlotChild(node);
  if (!loneChild || !isPanelNode(loneChild, interactionMode)) return node.slots;
  return [...(node.slots || []), ...(loneChild.slots || [])];
};

// Whether the Inspector's Link controls belong on THIS row.
//
// An element row owns them outright. A text or field row owns them only when it
// is its element's LONE content row. Every such row carries its element's
// layoutPatch — that is how the bridge marks a leaf's own runs — so wrapping
// from any of them wraps the whole element. That only reads as "link this
// thing" when the row IS the whole thing: for one run of `<p>Read <span>this
// </span> now</p>`, the panel shows a single Value input holding `now` while
// the wrap would swallow the paragraph and the span with it.
//
// "Lone" is findLoneTextSlotChild, the same test getPanelSlots uses to pull a
// run's Value onto its element's panel.
const ownsElementLinkControls = (
  node: LayersTreeNode,
  parentById: Map<string, LayersTreeNode | null>
): boolean => {
  if (node.kind === "element") return true;
  const layoutId = node.layoutPatch?.layoutId;
  if (!layoutId) return false;

  let host = parentById.get(node.id) || null;
  while (host && !(host.kind === "element" && host.layoutId === layoutId)) {
    host = parentById.get(host.id) || null;
  }
  if (!host) return false;

  return findLoneTextSlotChild(host)?.id === node.id;
};

export const useStudioLayersTree = ({
  interactionMode,
  postCommandToBridge,
  applyBridgeSelection,
  applyLayoutSelection,
  applyInspectorSelection,
  refreshInspectorSlots,
  codeFileNameById,
  fieldsState,
  connectedBySlot,
  selectedElement,
  selectedLayout,
  inspectorSelection,
  dndDisabled,
}: Args) => {
  const { t } = useTranslation();
  const [tree, setTree] = useState<LayersTreeNode[] | null>(null);
  // Rows the user has flipped AWAY from their default expansion. Root rows
  // default OPEN (a fresh panel shows one level); everything else defaults
  // CLOSED. Storing the delta rather than an absolute set keeps both defaults
  // applying to nodes that first appear in later re-emits.
  const [toggledIds, setToggledIds] = useState<Set<string>>(new Set());

  const handleLayersTree = useCallback((msg: any) => {
    const nextTree: LayersTreeNode[] = Array.isArray(msg?.tree)
      ? msg.tree.filter(isLayersTreeNode)
      : [];
    setTree(withTextElementPlaceholders(nextTree, null));
  }, []);

  const resetTree = useCallback(() => {
    setTree(null);
    setToggledIds(new Set());
  }, []);

  const { nodeById, parentById } = useMemo(() => {
    const byId = new Map<string, LayersTreeNode>();
    const parents = new Map<string, LayersTreeNode | null>();
    const index = (nodes: LayersTreeNode[], parent: LayersTreeNode | null) => {
      nodes.forEach((node) => {
        byId.set(node.id, node);
        parents.set(node.id, parent);
        index(node.children, node);
      });
    };
    index(tree || [], null);
    return { nodeById: byId, parentById: parents };
  }, [tree]);

  // Whether a row is expanded before the user ever touches it: only the
  // tree's root rows (no parent) are open, showing one level; everything
  // below starts closed.
  const isDefaultOpen = useCallback(
    (node: LayersTreeNode) => !parentById.get(node.id),
    [parentById]
  );

  const isNodeExpanded = useCallback(
    (node: LayersTreeNode) =>
      isDefaultOpen(node) ? !toggledIds.has(node.id) : toggledIds.has(node.id),
    [isDefaultOpen, toggledIds]
  );

  const selectedNodeId = useMemo(() => {
    // An open Inspector panel owns the highlight in both modes.
    if (inspectorSelection) return inspectorSelection.nodeId;

    if (usesLayoutGrammar(interactionMode)) {
      if (!selectedLayout?.layoutId) return null;
      for (const node of nodeById.values()) {
        if (
          node.kind === "element" &&
          node.layoutId === selectedLayout.layoutId &&
          node.codeId === selectedLayout.codeId
        ) {
          return node.id;
        }
      }
      return null;
    }

    if (!selectedElement?.fieldZuid) return null;
    let fallbackId: string | null = null;
    for (const node of nodeById.values()) {
      if (node.kind !== "field") continue;
      // Match studioId AND fieldZuid: one element can bind several attributes
      // (e.g. img src + alt) under a single studioId, so studioId alone is
      // ambiguous — the fieldZuid disambiguates which binding is selected.
      if (
        selectedElement.studioId &&
        node.studioId === selectedElement.studioId &&
        node.fieldZuid === selectedElement.fieldZuid
      ) {
        return node.id;
      }
      if (
        !fallbackId &&
        node.fieldZuid === selectedElement.fieldZuid &&
        (!selectedElement.itemZuid ||
          node.itemZuid === selectedElement.itemZuid)
      ) {
        fallbackId = node.id;
      }
    }
    return fallbackId;
  }, [
    interactionMode,
    nodeById,
    inspectorSelection,
    selectedElement,
    selectedLayout,
  ]);

  // Whether the selected layout element is in the tree and has nothing the
  // Inspector can edit. False before the tree arrives or for a panel-worthy
  // element, so a closed panel never reads as "no editable properties".
  const selectedLayoutHasNoPanel = useMemo(() => {
    if (!usesLayoutGrammar(interactionMode) || !selectedLayout?.layoutId) {
      return false;
    }
    for (const node of nodeById.values()) {
      if (
        node.kind === "element" &&
        node.layoutId === selectedLayout.layoutId &&
        node.codeId === selectedLayout.codeId
      ) {
        return !findLayoutPanelTarget(node, interactionMode);
      }
    }
    return false;
  }, [interactionMode, nodeById, selectedLayout]);

  // A selection (canvas click, drill-down, inspector open) can land on a row
  // whose ancestors are collapsed — expand them so the selected row actually
  // renders; the panel's scroll-into-view can't reach an unrendered row.
  // Deliberately NOT keyed on toggledIds: collapsing an ancestor of the
  // current selection afterwards is a user choice this must not fight.
  useEffect(() => {
    if (!selectedNodeId) return;
    setToggledIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      let parent = parentById.get(selectedNodeId) || null;
      while (parent) {
        // Force-expand relative to each row's default: a default-open row
        // expands by REMOVING its toggle, a default-closed one by ADDING it.
        if (isDefaultOpen(parent)) {
          if (next.delete(parent.id)) changed = true;
        } else if (!next.has(parent.id)) {
          next.add(parent.id);
          changed = true;
        }
        parent = parentById.get(parent.id) || null;
      }
      return changed ? next : prev;
    });
  }, [isDefaultOpen, parentById, selectedNodeId]);

  const toggleNode = useCallback((nodeId: string) => {
    setToggledIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const getRowLabel = useCallback(
    (node: LayersTreeNode): string => {
      if (node.kind === "codeFile") {
        return node.codeId
          ? codeFileNameById[node.codeId] || node.codeId
          : t("content.studioLayersNodePage");
      }
      if (node.kind === "element") {
        // The placeholder is a text element that has no tag yet — the tree calls
        // it what it is. "No Tag" is a statement about its tag, and that belongs
        // in the Tag selector, not in the row's name.
        if (node.tagName === NO_TAG) return t("content.studioLayersNodeText");
        return node.tagName || t("content.studioLayersNodeElement");
      }
      if (node.kind === "field") {
        // Dynamic content is labeled by its field name, not its value.
        const field = node.fieldZuid ? fieldsState[node.fieldZuid] : null;
        if (field?.label || field?.name) return field.label || field.name;

        // A freshly connected slot has no fieldZuid — the bridge is handed the
        // Parsley expression and never learns which field it refers to. Use the
        // ConnectField the user actually picked.
        // A field row represents one binding, so look up THAT slot rather than
        // scanning the element's slots — a prefix match could label a `src` row
        // with the field picked for `alt`.
        const slotKey = node.slots?.[0]?.key;
        const picked =
          node.layoutPatch && slotKey
            ? connectedBySlot[
                `${node.layoutPatch.codeId}::${node.layoutPatch.layoutId}::${slotKey}`
              ]
            : undefined;
        if (picked) return picked.label || picked.name;

        // Nothing recorded (a binding that predates this session reaches here
        // only if its markers are missing) — name it from the expression.
        // An item-level reference (a link's getUrl()) names no field, so there
        // is no name to fall back on — keep going rather than label the row "".
        const ref = parseParsleyRef(node.slots?.[0]?.sourceValue || "");
        if (ref)
          return (
            ref.name || node.fieldType || t("content.studioLayersNodeField")
          );

        return node.fieldType || t("content.studioLayersNodeField");
      }
      // Static text shows its actual content.
      return node.label || "";
    },
    [codeFileNameById, connectedBySlot, fieldsState, t]
  );

  const isNodeSelectable = useCallback(
    (node: LayersTreeNode) => {
      // Elements with editable attributes (e.g. <img>) are clickable in both
      // modes to open the Inspector panel.
      if (isPanelNode(node, interactionMode)) return true;
      if (usesLayoutGrammar(interactionMode)) {
        // Elements select on the canvas; static text rows are clickable to
        // enter inline static editing on their enclosing element.
        if (node.kind === "element") return !!node.layoutId && !!node.codeId;
        return node.kind === "text";
      }
      return node.kind === "field" && !!node.fieldZuid;
    },
    [interactionMode]
  );

  const flatRows = useMemo<LayersFlatRow[]>(() => {
    const rows: LayersFlatRow[] = [];
    const visit = (
      nodes: LayersTreeNode[],
      depth: number,
      parentTagName: string | null
    ) => {
      nodes.forEach((node) => {
        const hasChildren = node.children.length > 0;
        const expanded = isNodeExpanded(node);
        rows.push({
          node,
          depth,
          parentTagName,
          label: getRowLabel(node),
          hasChildren,
          expanded,
          selected: node.id === selectedNodeId,
          selectable: isNodeSelectable(node),
          draggable:
            !dndDisabled &&
            usesLayoutGrammar(interactionMode) &&
            node.kind === "element" &&
            !!node.layoutId &&
            !!node.codeId,
        });
        if (hasChildren && expanded) {
          visit(node.children, depth + 1, node.tagName);
        }
      });
    };
    visit(tree || [], 0, null);
    return rows;
  }, [
    dndDisabled,
    getRowLabel,
    interactionMode,
    isNodeExpanded,
    isNodeSelectable,
    selectedNodeId,
    tree,
  ]);

  // Select a layout element on the canvas, building the breadcrumb from tree
  // ancestors so it matches a canvas click (header breadcrumb + bridge state).
  const selectLayoutElement = useCallback(
    (node: LayersTreeNode) => {
      const breadcrumb: { layoutId?: string; label: string }[] = [
        {
          layoutId: node.layoutId!,
          label: node.tagName || t("content.studioLayersNodeElement"),
        },
      ];
      let parent = parentById.get(node.id) || null;
      while (parent) {
        if (parent.kind === "element" && parent.layoutId) {
          breadcrumb.unshift({
            layoutId: parent.layoutId,
            label: parent.tagName || t("content.studioLayersNodeElement"),
          });
        }
        parent = parentById.get(parent.id) || null;
      }

      applyLayoutSelection({
        codeId: node.codeId!,
        layoutId: node.layoutId!,
        breadcrumb,
      });
      postCommandToBridge({
        action: "setSelectedLayoutId",
        codeId: node.codeId!,
        layoutId: node.layoutId!,
      });
    },
    [applyLayoutSelection, parentById, postCommandToBridge, t]
  );

  // Pick which of a slot's two values the panel shows, by mode:
  //   layout  — the RAW template value (what you're actually editing). Never the
  //             resolved output, which would bake over a Parsley binding.
  //   content — the resolved value; a field-bound slot shows the field's NAME
  //             (mirrors the layers-row label) and clicks through to its editor.
  const resolveSlotValues = useCallback(
    (slots: ElementSlot[] | undefined): ElementSlot[] =>
      (slots || []).map((slot) => {
        if (usesLayoutGrammar(interactionMode)) {
          return slot.sourceValue !== undefined
            ? { ...slot, value: slot.sourceValue }
            : slot;
        }
        if (!slot.isDynamic || !slot.fieldZuid) return slot;
        const field = fieldsState[slot.fieldZuid];
        const fieldName = field?.label || field?.name;
        return fieldName ? { ...slot, value: fieldName } : slot;
      }),
    [fieldsState, interactionMode]
  );

  // Open the Inspector for a canvas-selected layout element, mirroring the
  // layers tree (layout mode only — content-mode canvas clicks route straight
  // to the field editor). Which panel opens depends on the element:
  //   - A supported element (an <img>, an <a>, an <h1>) opens ITS panel, like
  //     clicking its tree row. When its content is a lone text run or bound
  //     field, that row's Value slot is on the panel too (getPanelSlots), so
  //     an <h1> shows Tag and Value together.
  //   - An element the bridge offers no panel for (no layoutPatch of its own)
  //     falls back to its lone text run's / bound field's panel — titled
  //     "Text", holding the Value input — like clicking that child's tree row.
  //     The canvas can't select the child directly, so the element click
  //     stands in for it.
  // No-op when the tree hasn't arrived yet or nothing is panel-worthy; the
  // selection stands and any open Inspector was already closed by
  // applyLayoutSelection.
  const openInspectorForLayoutElement = useCallback(
    (codeId: string, layoutId: string) => {
      if (!usesLayoutGrammar(interactionMode)) return;
      for (const node of nodeById.values()) {
        if (
          node.kind === "element" &&
          node.layoutId === layoutId &&
          node.codeId === codeId
        ) {
          const target = findLayoutPanelTarget(node, interactionMode);
          if (!target) return;
          applyInspectorSelection({
            nodeId: target.id,
            tagName: target.tagName || "",
            slots: resolveSlotValues(getPanelSlots(target, interactionMode)),
            layoutPatch: target.layoutPatch ?? null,
            // Always true on this path — `target` is either the element or the
            // lone content row findLoneTextSlotChild just returned — but it is
            // derived rather than hardcoded so the two stay in step.
            ownsLinkControls: ownsElementLinkControls(target, parentById),
          });
          return;
        }
      }
    },
    [
      applyInspectorSelection,
      interactionMode,
      nodeById,
      parentById,
      resolveSlotValues,
    ]
  );

  const handleNodeSelect = useCallback(
    (node: LayersTreeNode) => {
      // Elements (Tag selector + attributes) and editable text nodes (Text
      // input) open the Inspector panel in both modes. In layout mode an
      // element also selects on the canvas so the breadcrumb + outline follow,
      // and carries its lone text run's Value slot (getPanelSlots) — the same
      // panel a canvas click opens; a text node has no layoutId so it skips
      // that, and still opens its own panel so each run of mixed content stays
      // editable. An empty tagName marks a text node — the panel titles it
      // "Text" and hides the Tag selector.
      if (isPanelNode(node, interactionMode)) {
        if (
          usesLayoutGrammar(interactionMode) &&
          node.layoutId &&
          node.codeId
        ) {
          selectLayoutElement(node);
        }
        applyInspectorSelection({
          nodeId: node.id,
          tagName: node.tagName || "",
          slots: resolveSlotValues(getPanelSlots(node, interactionMode)),
          layoutPatch: node.layoutPatch ?? null,
          ownsLinkControls: ownsElementLinkControls(node, parentById),
        });
        return;
      }

      // Non-editable static text in layout mode → enter inline static editing
      // on the nearest enclosing element (mirrors double-clicking on canvas).
      if (usesLayoutGrammar(interactionMode) && node.kind === "text") {
        let host = parentById.get(node.id) || null;
        while (
          host &&
          !(host.kind === "element" && host.layoutId && host.codeId)
        ) {
          host = parentById.get(host.id) || null;
        }
        if (!host) return;
        selectLayoutElement(host);
        postCommandToBridge({
          action: "enterStaticEditingByLayoutId",
          codeId: host.codeId!,
          layoutId: host.layoutId!,
        });
        return;
      }

      if (!isNodeSelectable(node)) {
        if (node.children.length) toggleNode(node.id);
        return;
      }

      if (usesLayoutGrammar(interactionMode)) {
        selectLayoutElement(node);
        // An element with no panel of its own (`<small>fine print</small>`)
        // opens its lone text run's panel, as a canvas click on it does.
        const target =
          node.kind === "element"
            ? findLayoutPanelTarget(node, interactionMode)
            : null;
        if (target) {
          applyInspectorSelection({
            nodeId: target.id,
            tagName: target.tagName || "",
            slots: resolveSlotValues(getPanelSlots(target, interactionMode)),
            layoutPatch: target.layoutPatch ?? null,
            ownsLinkControls: ownsElementLinkControls(target, parentById),
          });
        }
        return;
      }

      applyBridgeSelection({
        studioId: node.studioId,
        fieldZuid: node.fieldZuid!,
        fieldType: node.fieldType,
        itemZuid: node.itemZuid,
        modelZuid: node.modelZuid,
      });
    },
    [
      applyInspectorSelection,
      applyBridgeSelection,
      interactionMode,
      isNodeSelectable,
      parentById,
      postCommandToBridge,
      resolveSlotValues,
      selectLayoutElement,
      toggleNode,
    ]
  );

  // When the tree re-emits (e.g. after a tag swap changes which slots exist),
  // refresh the open panel's element so it shows the new type's slots. Built
  // with the same getPanelSlots as the selection, or an element panel would
  // drop its lone text run's Value on the first re-emit.
  useEffect(() => {
    const nodeId = inspectorSelection?.nodeId;
    if (!nodeId) return;
    const node = nodeById.get(nodeId);
    if (!node || !isPanelNode(node, interactionMode)) return;
    const slots = resolveSlotValues(getPanelSlots(node, interactionMode));
    // Clearing the Value makes the bridge re-emit the element with no content
    // row, so keep the panel's Value slot or the input unmounts mid-edit. Only
    // for an element left with NO content rows: one that now has several runs
    // is mixed content, edited run by run on its own rows, and a carried Value
    // would be a stale copy of the single run it used to have.
    const prevText = inspectorSelection?.slots.find((s) => s.key === "text");
    if (
      node.kind === "element" &&
      prevText &&
      !slots.some((s) => s.key === "text") &&
      countTextSlotChildren(node) === 0
    ) {
      slots.push(prevText);
    }
    refreshInspectorSlots(
      nodeId,
      // Empty tagName marks a text/field row — the panel titles it "Text" and
      // hides the Tag selector.
      node.tagName || "",
      slots,
      node.layoutPatch ?? null,
      // A re-emit can add or remove sibling runs, so which row owns the Link
      // controls is re-derived rather than carried over.
      ownsElementLinkControls(node, parentById),
      // Only layout mode holds editable values worth protecting from a re-emit.
      usesLayoutGrammar(interactionMode)
    );
  }, [
    interactionMode,
    nodeById,
    parentById,
    refreshInspectorSlots,
    resolveSlotValues,
    inspectorSelection,
  ]);

  const canDrop = useCallback(
    (
      sourceId: string,
      targetId: string,
      position: LayersDropPosition
    ): boolean => {
      if (dndDisabled || !usesLayoutGrammar(interactionMode)) return false;
      const source = nodeById.get(sourceId);
      const target = nodeById.get(targetId);
      if (!source || !target || source === target) return false;
      if (source.kind !== "element" || target.kind !== "element") return false;
      if (!source.layoutId || !source.codeId || !target.layoutId) return false;
      // Reordering relative to a target keeps it in its parent; dropping
      // before/after a region root would move the element outside its code
      // region, so require a codeId on the target either way.
      if (!target.codeId) return false;

      // A node can't be dropped into its own subtree.
      let parent = parentById.get(targetId) || null;
      while (parent) {
        if (parent.id === sourceId) return false;
        parent = parentById.get(parent.id) || null;
      }

      if (
        position === "inside" &&
        target.tagName &&
        VOID_ELEMENT_TAGS.has(target.tagName)
      ) {
        return false;
      }

      return true;
    },
    [dndDisabled, interactionMode, nodeById, parentById]
  );

  const handleNodeDrop = useCallback(
    (sourceId: string, targetId: string, position: LayersDropPosition) => {
      if (!canDrop(sourceId, targetId, position)) return;
      const source = nodeById.get(sourceId)!;
      const target = nodeById.get(targetId)!;

      // Select the dragged element first — canvas drags require selection,
      // and the reorder pipeline re-roots the current selection's breadcrumb.
      // Select it as a layout element directly (not via handleNodeSelect) so a
      // draggable img doesn't pop open the Inspector panel mid-drag.
      selectLayoutElement(source);

      postCommandToBridge({
        action: "moveLayoutElement",
        layoutId: source.layoutId,
        codeId: source.codeId,
        targetLayoutId: target.layoutId,
        targetCodeId: target.codeId,
        position,
      });
    },
    [canDrop, nodeById, postCommandToBridge, selectLayoutElement]
  );

  return {
    hasTree: tree !== null,
    flatRows,
    selectedNodeId,
    selectedLayoutHasNoPanel,
    handleLayersTree,
    resetTree,
    toggleNode,
    handleNodeSelect,
    openInspectorForLayoutElement,
    canDrop,
    handleNodeDrop,
  };
};
